/**
 * BotSpot Web Dashboard Bots API
 *
 * API functions for bot lifecycle management.
 */

import { apiClient } from "./client";
import type {
  Bot,
  BotCreate,
  BotUpdate,
  BotList,
  BotHealth,
  BotMetrics,
  MessageResponse,
  PaginationParams,
  LogList,
  ChatResponse,
} from "./types";

/**
 * List all bots for the current user
 *
 * @param params - Pagination parameters
 * @returns Paginated list of bots
 */
export async function listBots(params?: PaginationParams): Promise<BotList> {
  const queryParams = new URLSearchParams();
  if (params?.page !== undefined) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.page_size !== undefined) {
    queryParams.append("page_size", params.page_size.toString());
  }

  const url = `/bots${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  return await apiClient.get<BotList>(url);
}

/**
 * Get details of a specific bot
 *
 * @param botId - Bot ID
 * @returns Bot details
 */
export async function getBot(botId: string): Promise<Bot> {
  return await apiClient.get<Bot>(`/bots/${botId}`);
}

/**
 * Create a new bot instance
 *
 * @param botData - Bot creation data
 * @returns Created bot object
 */
export async function createBot(botData: BotCreate): Promise<Bot> {
  return await apiClient.post<Bot>("/bots", botData);
}

/**
 * Update an existing bot's configuration
 *
 * @param botId - Bot ID
 * @param updates - Bot update data
 * @returns Updated bot object
 */
export async function updateBot(botId: string, updates: BotUpdate): Promise<Bot> {
  return await apiClient.patch<Bot>(`/bots/${botId}`, updates);
}

/**
 * Delete/destroy a bot instance
 *
 * @param botId - Bot ID
 * @returns Success message
 */
export async function deleteBot(botId: string): Promise<MessageResponse> {
  return await apiClient.delete<MessageResponse>(`/bots/${botId}`);
}

/**
 * Start a stopped bot instance
 *
 * @param botId - Bot ID
 * @returns Updated bot object with new status
 */
export async function startBot(botId: string): Promise<Bot> {
  return await apiClient.post<Bot>(`/bots/${botId}/start`);
}

/**
 * Stop a running bot instance
 *
 * @param botId - Bot ID
 * @returns Updated bot object with new status
 */
export async function stopBot(botId: string): Promise<Bot> {
  return await apiClient.post<Bot>(`/bots/${botId}/stop`);
}

/**
 * Restart a bot instance
 *
 * @param botId - Bot ID
 * @returns Updated bot object with new status
 */
export async function restartBot(botId: string): Promise<Bot> {
  return await apiClient.post<Bot>(`/bots/${botId}/restart`);
}

/**
 * Get health status of a bot
 *
 * @param botId - Bot ID
 * @returns Bot health information
 */
export async function getBotHealth(botId: string): Promise<BotHealth> {
  return await apiClient.get<BotHealth>(`/bots/${botId}/health`);
}

/**
 * Get bot logs
 *
 * Retrieves recent log entries for a bot instance.
 *
 * @param botId - Bot ID
 * @param params - Log query parameters
 * @returns Log list with structured entries
 */
export async function getBotLogs(
  botId: string,
  params?: {
    lines?: number;
  }
): Promise<LogList> {
  const queryParams = new URLSearchParams();
  if (params?.lines !== undefined) {
    queryParams.append("lines", params.lines.toString());
  }

  const url = `/bots/${botId}/logs${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  return await apiClient.get<LogList>(url);
}

/**
 * Get bot resource metrics
 *
 * Retrieves current resource utilization metrics for a bot including
 * CPU, memory, network, and disk statistics.
 *
 * @param botId - Bot ID
 * @returns Current bot metrics
 */
export async function getBotMetrics(botId: string): Promise<BotMetrics> {
  return await apiClient.get<BotMetrics>(`/bots/${botId}/metrics`);
}

/**
 * Send a message to a running bot
 *
 * @param botId - Bot ID
 * @param message - Message to send
 * @returns Bot response
 */
export async function sendBotMessage(
  botId: string,
  message: string
): Promise<ChatResponse> {
  return await apiClient.post<ChatResponse>(`/bots/${botId}/messages`, {
    message,
  });
}

/**
 * Execute a command in a bot instance
 *
 * Note: This endpoint may not be implemented in the current backend.
 * This is a placeholder for future implementation.
 *
 * @param botId - Bot ID
 * @param command - Command to execute
 * @returns Command execution result
 */
export async function executeBotCommand(
  botId: string,
  command: string
): Promise<{
  output: string;
  exit_code: number;
}> {
  return await apiClient.post(`/bots/${botId}/exec`, {
    command,
  });
}

/**
 * Install a skill on a bot
 *
 * Note: This endpoint may not be implemented in the current backend.
 * This is a placeholder for future implementation.
 *
 * @param botId - Bot ID
 * @param skillName - Name of the skill to install
 * @param skillVersion - Optional version to install
 * @returns Success message
 */
export async function installBotSkill(
  botId: string,
  skillName: string,
  skillVersion?: string
): Promise<MessageResponse> {
  return await apiClient.post<MessageResponse>(`/bots/${botId}/skills`, {
    name: skillName,
    version: skillVersion,
  });
}

/**
 * Uninstall a skill from a bot
 *
 * Note: This endpoint may not be implemented in the current backend.
 * This is a placeholder for future implementation.
 *
 * @param botId - Bot ID
 * @param skillName - Name of the skill to uninstall
 * @returns Success message
 */
export async function uninstallBotSkill(
  botId: string,
  skillName: string
): Promise<MessageResponse> {
  return await apiClient.delete<MessageResponse>(
    `/bots/${botId}/skills/${skillName}`
  );
}
